import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import { useRouteParams } from '@vueuse/router'
import { defu } from 'defu'
import { useUserStore } from './user'
import type {
  AddUsersToWorkspaceResponse,
  Workspace,
  WorkspaceWithUsers,
} from '~/types/Workspace'
import {
  workspaceCreateDefault,
  workspaceDefault,
} from '~/static/data/workspace/workspaceDefaults'
import { StateError } from '~/errors/StateError'
import { ArgumentError } from '~/errors/ArgumentError'
import { NotFoundError } from '~/errors/NotFoundError'
import { NetworkError } from '~/errors/NetworkError'
import type { User } from '~/types/User'
import { WorkspaceService } from '~/services/api/workspace/workspace'
import { organizationKeys } from '~/queries/organization/keys'
import { systemKeys } from '~/queries/system/keys'
import { useWorkspaceSseStore } from '~/stores/workspace/workspace-sse'

export const useWorkspaceStore = defineStore('workspace', () => {
  const workspaceService = new WorkspaceService()
  const queryClient = useOptionalQueryClient()
  const { addToast } = useToastNotifications()

  const router = useRouter()

  // OTHER STORES ========================================================================
  const userStore = useUserStore()
  nextTick(() => useWorkspaceSseStore())

  // STATE ========================================================================
  const workspaceIdInRoute = useRouteParams('workspaceId')
  const currentWorkspace = ref<WorkspaceWithUsers | null>(null)
  const currentWorkspaceId = ref<string | null>(null)
  const workspaceUsers = computed(() => {
    if (!currentWorkspace.value) return []

    return currentWorkspace.value.users
  })
  const workspacePreviousUsers = computed<User[]>(() => {
    if (!currentWorkspace.value) return []

    return currentWorkspace.value.previousUsers
  })
  const workspaceHasActiveLtiPlatforms = computed(() => {
    if (!currentWorkspace.value) return false

    if (!currentWorkspace.value.ltiPlatforms?.length) return false

    return currentWorkspace.value.ltiPlatforms.some(
      (platform) => platform.isActive,
    )
  })

  const useGradingVariabilityDetection = computed(
    () => currentWorkspace.value?.useGradingVariabilityDetection ?? false,
  )

  // CRUD ========================================================================

  async function updateWorkspace(workspaceData: Partial<Workspace>) {
    if (!currentWorkspaceId.value)
      return {
        data: null,
        error: new StateError(
          'Could not update workspace because no workspace ID is defined.',
        ),
      }

    const { data, error } = await workspaceService.updateWorkspaceById(
      currentWorkspaceId.value,
      workspaceData,
    )

    if (error !== null) {
      return { data, error }
    }

    currentWorkspace.value = { ...currentWorkspace.value!, ...workspaceData }

    // Title/image also render on the query-backed org Workspaces list and the
    // system-admin all-workspaces list — the local currentWorkspace update above
    // only refreshes this settings page. No orgId in scope, so bust every cached
    // org-workspaces list by predicate.
    queryClient?.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'organizations' &&
        query.queryKey[2] === 'workspaces',
    })
    queryClient?.invalidateQueries({ queryKey: systemKeys.workspaces() })

    return { data, error }
  }

  async function createWorkspace(workspaceTitle: string) {
    if (!userStore.user?.id) {
      return {
        data: null,
        error: new StateError(
          `Could not create workspace with title ${workspaceTitle} becuase no user is defined.`,
        ),
      }
    }

    if (!workspaceTitle) {
      return {
        data: null,
        error: new ArgumentError(
          'The workspace could not be created because no title was defined.',
        ),
      }
    }

    const { data, error } = await workspaceService.postWorkspace({
      ...workspaceCreateDefault,
      title: workspaceTitle,
      createdBy: userStore.user.id,
    })

    if (error !== null) {
      return { data, error }
    }

    await userStore.$refresh()

    // $refresh only reloads the /me store (home/nav list). The org settings
    // Workspaces list + summary card and the system-admin all-orgs/all-workspaces
    // /summary surfaces are query-backed, so bust those too — no orgId in scope
    // here, so match the org-scoped keys by predicate.
    queryClient?.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'organizations' &&
        (query.queryKey[2] === 'workspaces' || query.queryKey[2] === 'summary'),
    })
    queryClient?.invalidateQueries({ queryKey: organizationKeys.all() })
    queryClient?.invalidateQueries({ queryKey: systemKeys.workspaces() })
    queryClient?.invalidateQueries({ queryKey: systemKeys.summary() })

    return { data, error }
  }

  async function inviteUsersToWorkspace(userEmails: string[], note?: string) {
    if (!currentWorkspaceId.value)
      return {
        data: null,
        error: new StateError(
          'Could not add users to workspace because no workspace ID is defined.',
        ),
      }

    const { data, error } = await workspaceService.inviteUsersToWorkspace(
      currentWorkspaceId.value,
      userEmails,
      note,
    )

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Could not add users to workspace',
        message:
          'There was an error while adding users to the workspace. Please try again.',
        life: 5000,
      })
      return { data: null, error }
    }

    await $refresh()

    _showAddUsersToast(userEmails, data)
  }

  function _showAddUsersToast(
    userEmails: string[],
    result: AddUsersToWorkspaceResponse,
  ) {
    if (userEmails.length === 1) {
      const email = userEmails[0]

      if (result.failed.length > 0) {
        addToast({
          type: 'error',
          heading: 'Failed to add user',
          message: `${email} could not be added to the workspace.`,
          life: 5000,
        })
      } else if (result.added.length > 0) {
        addToast({
          type: 'default',
          heading: 'User added',
          message: `${email} has been added to the workspace.`,
          life: 5000,
        })
      } else if (result.invited.length > 0) {
        addToast({
          type: 'default',
          heading: 'User invited',
          message: `${email} has been invited to the workspace.`,
          life: 5000,
        })
      } else if (result.reinvited.length > 0) {
        addToast({
          type: 'default',
          heading: 'User re-invited',
          message: `${email} has been re-invited to the workspace.`,
          life: 5000,
        })
      }

      return
    }

    const parts: string[] = []
    if (result.added.length > 0) parts.push(`${result.added.length} added`)
    if (result.invited.length > 0)
      parts.push(`${result.invited.length} invited`)
    if (result.reinvited.length > 0)
      parts.push(`${result.reinvited.length} re-invited`)
    if (result.failed.length > 0) parts.push(`${result.failed.length} failed`)

    const allFailed = result.failed.length === userEmails.length

    addToast({
      type: allFailed ? 'error' : 'default',
      heading: allFailed ? 'Failed to add users' : 'Users processed',
      message: parts.join(', '),
      life: 5000,
    })
  }

  function getWorkspaceUserById(userId: string) {
    let response = workspaceUsers.value.find((user) => user.id === userId)

    if (!response) {
      response = workspacePreviousUsers.value.find((user) => user.id === userId)
    }

    if (!response) {
      Sentry.captureException(
        new NotFoundError(
          `User with ID ${userId} not found in workspace ${currentWorkspaceId.value}.`,
        ),
      )
    }

    return response
  }

  async function removeUserFromWorkspace(userId: string) {
    if (!currentWorkspaceId.value || !currentWorkspace.value)
      return {
        data: null,
        error: new StateError(
          'Could not remove user from workspace because no workspace ID is defined.',
        ),
      }

    const { error } = await workspaceService.removeUserFromWorkspace(
      currentWorkspaceId.value,
      userId,
    )

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Could not remove user from workspace',
        message:
          'There was an error while removing the user from the workspace. Please try again.',
        life: 5000,
      })
      return { error }
    }

    currentWorkspace.value.users = currentWorkspace.value!.users.filter(
      (user) => user.id !== userId,
    )

    const removedUser = currentWorkspace.value.users.find(
      (user) => user.id === userId,
    )

    if (!removedUser) {
      Sentry.captureException(
        new NotFoundError(
          `User with ID ${userId} not found in workspace ${currentWorkspaceId.value}.`,
        ),
      )
      return { error }
    }

    currentWorkspace.value.previousUsers.push(removedUser)

    return { error }
  }

  // API CALLS ========================================================================
  async function fetchWorkspaceFromServer(workspaceId: string) {
    if (!workspaceId)
      return {
        data: null,
        error: new ArgumentError(
          'Could not fetch workspace from server. Workspace ID required.',
        ),
      }

    const { data, error } = await workspaceService.getWorkspaceById(workspaceId)

    return { data, error }
  }

  // META ACTIONS ========================================================================
  function reset() {
    currentWorkspace.value = null
    currentWorkspaceId.value = null
  }

  const {
    $isInitialised,
    $isInitialising,
    $isUpdating,
    $initialise,
    $ensureInitialised,
    $refresh,
    $reset,
  } = useStoreManager(
    computed(() => workspaceIdInRoute.value),

    async () => {
      if (!workspaceIdInRoute.value) return false

      currentWorkspaceId.value = workspaceIdInRoute.value as string

      const { data, error } = await fetchWorkspaceFromServer(
        currentWorkspaceId.value,
      )

      if (error !== null) {
        if (error instanceof NetworkError) {
          if (error.statusCode === 404) {
            showError({
              statusCode: 404,
              statusMessage: 'Workspace not found',
              message: `Workspace with ID ${currentWorkspaceId.value} not found`,
            })
            reset()
            return false
          }
        }
        addToast({
          type: 'error',
          heading: 'Error loading workspace',
          message: `The workspace you were looking for could not be found. Please double check the URL and try again.`,
          life: 5000,
        })
        reset()
        router.push('/')
        return false
      }

      // Workspace membership is enforced upstream by the access gate
      // (05.access middleware → 403), so by the time the fetch succeeds the
      // user is guaranteed to belong. The store is a pure data fetch.
      currentWorkspace.value = defu(
        data,
        workspaceDefault,
      ) as WorkspaceWithUsers

      return true
    },

    reset,

    async () => {
      if (!currentWorkspaceId.value) return

      const { data, error } = await fetchWorkspaceFromServer(
        currentWorkspaceId.value,
      )

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error refreshing workspace',
          message:
            'The workspace could not be refreshed. If the problem persists, please try reloading the page.',
          life: 5000,
        })
        return
      }

      currentWorkspace.value = defu(
        data,
        workspaceDefault,
      ) as WorkspaceWithUsers
    },
  )

  return {
    currentWorkspace,
    currentWorkspaceId,
    useGradingVariabilityDetection,
    createWorkspace,
    workspaceUsers,
    workspacePreviousUsers,
    updateWorkspace,
    inviteUsersToWorkspace,
    getWorkspaceUserById,
    removeUserFromWorkspace,
    $reset,
    $refresh,
    $initialise,
    $ensureInitialised,
    $isInitialising,
    $isUpdating,
    $isInitialised,
    workspaceHasActiveLtiPlatforms,
  }
})
